import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { format, parseISO } from 'date-fns';
import AdminDeadlineService from '../../services/AdminDeadlineService';

import {
  Plus,
  Pencil,
  Trash,
  Check,
  X
} from 'lucide-react';


export default function GlobalDeadlineAdmin() {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadColleges();
  }, []);

  const loadColleges = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('global_colleges')
        .select('*')
        .order('name');

      if (error) throw error;
      setColleges(data || []);
    } catch (error) {
      console.error('Error loading colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (college) => {
    setEditingId(college.id);
    setEditForm({
      ed_deadline: college.ed_deadline || '',
      ed2_deadline: college.ed2_deadline || '',
      ea_deadline: college.ea_deadline || '',
      rd_deadline: college.rd_deadline || '',
      finaid_deadline: college.finaid_deadline || '',
      css_deadline: college.css_deadline || '',
      merit_deadline: college.merit_deadline || '',
      honors_deadline: college.honors_deadline || ''
    });
  };

  const handleSave = async (collegeId) => {
    try {
      const { error } = await supabase
        .from('global_colleges')
        .update(editForm)
        .eq('id', collegeId);

      if (error) throw error;

      // Update local state
      setColleges(colleges.map(c => 
        c.id === collegeId ? { ...c, ...editForm } : c
      ));
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating college:', error);
      alert('Failed to update college deadlines');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handlePreviewUpdate = async () => {
    try {
      setBulkUpdating(true);
      const preview = await AdminDeadlineService.previewAcademicCycleUpdate(targetYear);
      setPreviewData(preview);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleConfirmUpdate = async () => {
    if (!window.confirm(`This will update ${previewData.collegesWithChanges} colleges to the ${previewData.cycleYear} cycle. Continue?`)) {
      return;
    }

    setBulkUpdating(true);
    try {
      const results = await AdminDeadlineService.bulkUpdateToAcademicCycle(targetYear);
      
      alert(`Successfully updated ${results.successful} colleges to ${results.cycleYear} cycle.\n${results.failed} failures.`);
      
      setShowPreview(false);
      setPreviewData(null);
      await loadColleges();
    } catch (error) {
      console.error('Error in bulk update:', error);
      alert('Bulk update failed: ' + error.message);
    } finally {
      setBulkUpdating(false);
    }
  };

  const filteredColleges = colleges.filter(college =>
    college.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                Preview Update to {previewData.cycleYear} Cycle
              </h2>
              <p className="text-slate-600 mt-1">
                {previewData.collegesWithChanges} colleges will be updated ({previewData.totalFieldsToUpdate} total changes)
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {previewData.changes.slice(0, 20).map((college, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">{college.collegeName}</h3>
                    <div className="space-y-1">
                      {college.updates.map((update, updateIdx) => (
                        <div key={updateIdx} className="text-sm">
                          <span className="font-medium">{update.field}:</span>{' '}
                          <span className="text-red-600 line-through">{update.from}</span>
                          {' → '}
                          <span className="text-emerald-600">{update.to}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {previewData.changes.length > 20 && (
                  <div className="text-center text-stone-500 py-2">
                    ... and {previewData.changes.length - 20} more colleges
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewData(null);
                }}
                className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpdate}
                disabled={bulkUpdating}
                className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-stone-400"
              >
                {bulkUpdating ? 'Updating...' : 'Confirm Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header and Controls */}
      <div className="flex justify-between items-center">
        <div className="flex-1 max-w-lg">
          <input
            type="text"
            placeholder="Search colleges..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
          />
        </div>
        <div className="ml-4 flex items-center gap-2">
          <select
            value={targetYear}
            onChange={(e) => setTargetYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500"
          >
            <option value={2025}>2025-2026 Cycle</option>
            <option value={2026}>2026-2027 Cycle</option>
            <option value={2027}>2027-2028 Cycle</option>
          </select>
          <button
            onClick={handlePreviewUpdate}
            disabled={bulkUpdating}
            className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-stone-400"
          >
            {bulkUpdating ? 'Loading...' : 'Preview Update'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold">{colleges.length}</div>
          <div className="text-slate-600">Total Colleges</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold">
            {colleges.filter(c => c.ed_deadline || c.ea_deadline).length}
          </div>
          <div className="text-slate-600">With Early Deadlines</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold">
            {colleges.filter(c => c.rd_deadline).length}
          </div>
          <div className="text-slate-600">With RD Deadline</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold">
            {colleges.filter(c => c.finaid_deadline || c.css_deadline).length}
          </div>
          <div className="text-slate-600">With Financial Aid</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  College
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  ED
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  ED2
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  EA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  RD
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Fin Aid
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  CSS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Merit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Honors
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-4 text-center text-stone-500">
                    Loading colleges...
                  </td>
                </tr>
              ) : filteredColleges.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-4 text-center text-stone-500">
                    No colleges found
                  </td>
                </tr>
              ) : (
                filteredColleges.map((college) => (
                  <tr key={college.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {college.name}
                    </td>
                    {editingId === college.id ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.ed_deadline}
                            onChange={(e) => setEditForm({...editForm, ed_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.ed2_deadline}
                            onChange={(e) => setEditForm({...editForm, ed2_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.ea_deadline}
                            onChange={(e) => setEditForm({...editForm, ea_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.rd_deadline}
                            onChange={(e) => setEditForm({...editForm, rd_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.finaid_deadline}
                            onChange={(e) => setEditForm({...editForm, finaid_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.css_deadline}
                            onChange={(e) => setEditForm({...editForm, css_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.merit_deadline}
                            onChange={(e) => setEditForm({...editForm, merit_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.honors_deadline}
                            onChange={(e) => setEditForm({...editForm, honors_deadline: e.target.value})}
                            className="text-sm border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleSave(college.id)}
                            className="text-emerald-600 hover:text-emerald-900 mr-2"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.ed_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.ed2_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.ea_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.rd_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.finaid_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.css_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.merit_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {formatDate(college.honors_deadline)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(college)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Pencil className="h-5 w-5" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}